import React, { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import { getRewardfulReferral } from "../../utils/rewardful";
import { AuthContext } from "../../context/AuthContext";
import { resetAnalytics, track } from "../../utils/analytics";
import { getRegion } from "../../utils/region";
import GiftOffer, { formatLeft } from "./GiftOffer";
import { openTrialCheckout } from "../../utils/trialCheckout";
import "./HardPaywall.css";

// Last step of signup: email/Google → onboarding → this. Leads with one offer:
// "Start for $1" (120 credits + a 3-day Pro trial, then Pro monthly), paid on
// Stripe's hosted Checkout (utils/trialCheckout.js). Founder lifetime is sold once they're in (UpgradeModal).
// Accounts that can't take the trial again (had one, or a subscription) get
// the two plans instead: Pro monthly ($29/mo, pre-selected) or the Founder
// lifetime deal ($200 once). It can be closed (onClose), but ForYouPage brings
// it back the moment they try to do anything.
// Light theme on purpose (white + orange over the dark app), sized to fit one
// screen on laptops and phones. Leads with the moat: clipping while you're live.

// Light, Sherlock-style paywall (white on orange) — a deliberate contrast to
// the dark app, and continuous with Stripe's white checkout page.
// Text lives in locales/<lang>/paywall.json.
const STATS = [
  { value: "100M+", label: "stat_views" },
  { valueKey: "stat_live_value", label: "stat_live" },
  { value: "300", label: "stat_credits" },
];

// Sold by impact (what it does FOR them), not by feature.
const FEATURES = [
  { label: "feat_daily", icon: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></> },
  { label: "feat_moment", icon: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /> },
  { label: "feat_grow", icon: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></> },
];


// Platforms we clip from — recognizable logos build trust.
const PLATFORMS = [
  { name: "Twitch", logo: "/twitch.png" },
  { name: "Kick", logo: "/kick.png" },
  { name: "YouTube", logo: "/youtube.png" },
];

// Twinkles scattered over the CTA (positions/timings staggered so they never
// all flash at once).
const SPARKLES = [
  { left: "5%", top: "20%", delay: "0s", size: "16px" },
  { left: "15%", top: "62%", delay: "0.8s", size: "11px" },
  { left: "27%", top: "12%", delay: "1.6s", size: "10px" },
  { left: "50%", top: "70%", delay: "2.2s", size: "9px" },
  { left: "68%", top: "66%", delay: "0.4s", size: "13px" },
  { left: "80%", top: "14%", delay: "1.2s", size: "16px" },
  { left: "91%", top: "56%", delay: "2s", size: "11px" },
  { left: "40%", top: "8%", delay: "2.6s", size: "8px" },
];

// iPhone + an in-app browser (Instagram, Facebook, TikTok, Snapchat…): Apple
// Pay doesn't work there, so card entry is the only option at checkout. We
// offer to reopen the app in Safari — already signed in via /auth-success —
// where Stripe shows Apple Pay.
const UA = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
const IS_IOS = /iPhone|iPad|iPod/i.test(UA);
const IN_APP_BROWSER = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|musical_ly|BytedanceWebview|TikTok|Snapchat|LinkedInApp|Twitter/i.test(UA);
const SHOW_SAFARI_PROMPT = (IS_IOS && IN_APP_BROWSER) ||
  (typeof window !== "undefined" && /[?&]safari_prompt=1/.test(window.location.search)); // ?safari_prompt=1 to preview

// Stripe sends them back here; ForYouPage waits for the webhook to activate
// the plan instead of flashing the paywall again.
const PRO_PRICE = 29;
const FOUNDER_PRICE = 200; // same everywhere — never regionally discounted
const GIFT_PCT = 25; // paywall gift (services/exitOffer.js): 25% off Pro's 1st month
const GIFT_LIFETIME = 150; // …and $150 Lifetime, while the 11h timer runs
export const money = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

const RETURN_URL = `${window.location.origin}/home?paywall=paid`;
const CANCEL_URL = `${window.location.origin}/home`;

// readyClips: the free "try it" run's finished clips → lead with them.
const HardPaywall = ({ activating = false, onClose, readyClips = [] }) => {
  const [zoomedClip, setZoomedClip] = useState(null); // tapped ready-clip card (grows a little)
  const [lockShake, setLockShake] = useState({ i: null, n: 0 }); // re-keys the lock so it jitters on every tap
  // Tap anywhere outside the clip cards → back to normal size.
  useEffect(() => {
    if (zoomedClip === null) return undefined;
    const close = (e) => { if (!(e.target instanceof Element && e.target.closest(".hpw-ready-clip"))) setZoomedClip(null); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [zoomedClip]);
  const { t, i18n } = useTranslation("paywall");
  const { dispatch } = useContext(AuthContext);
  // Pro Monthly is the highlighted, pre-selected pick.
  const [plan, setPlan] = useState("pro"); // "founder" | "pro"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [foundersOpen, setFoundersOpen] = useState(true);
  const [spotsLeft, setSpotsLeft] = useState(null);

  useEffect(() => {
    if (!activating) track("paywall_viewed");
  }, [activating]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/founders-spots")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.soldOut) setFoundersOpen(false);
        if (Number.isFinite(r.data?.remaining)) setSpotsLeft(r.data.remaining);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Real account count ("Join 4,200+ creators"), rounded down by the server.
  const [creators, setCreators] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/social-proof").then((r) => { if (!cancelled && r.data?.creators) setCreators(r.data.creators); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Regional (PPP) price for Pro — checkout applies the same discount server-side.
  const [discountPct, setDiscountPct] = useState(0);
  useEffect(() => {
    let cancelled = false;
    getRegion().then((r) => { if (!cancelled && r.discountPct > 0) setDiscountPct(r.discountPct); });
    return () => { cancelled = true; };
  }, []);
  const proPrice = Math.round(PRO_PRICE * (100 - discountPct)) / 100;

  // $1 trial (the default) unless the server says this account already had it.
  const [trialOk, setTrialOk] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/trial/eligibility")
      .then((r) => { if (!cancelled) setTrialOk(!!r.data?.eligible); })
      .catch(() => { if (!cancelled) setTrialOk(true); });
    return () => { cancelled = true; };
  }, []);
  const trialMode = trialOk !== false;

  // The stream they picked in onboarding, waiting to be clipped once they pay
  // (routes/freeRun.js). Shown here so "Start for $1" is about THEIR stream.
  const [pendingRun, setPendingRun] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/free-run/pending")
      .then((r) => { if (!cancelled && r.data?.pending) setPendingRun(r.data.pending); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const showPending = !!pendingRun && !readyClips.length;

  // ── Free gift on close (X) ──
  const [gift, setGift] = useState(null); // { active, offered, expiresAt }
  const [giftStage, setGiftStage] = useState(null); // null | "gift" | "reveal" | "leave"
  const [giftBusy, setGiftBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get("/billing/exit-offer").then((r) => { if (!cancelled) setGift(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const expiresAtMs = gift?.expiresAt ? new Date(gift.expiresAt).getTime() : 0;
  useEffect(() => {
    if (!expiresAtMs || expiresAtMs <= Date.now()) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAtMs]);
  const leftMs = Math.max(0, expiresAtMs - now);
  const giftOn = leftMs > 0 && !trialMode; // the gift discounts Pro / Lifetime, not the $1 trial
  // Checkout takes one discount: the gift only changes Pro when it beats the
  // regional price. Lifetime always drops to $150 while it's live.
  const proGift = giftOn && discountPct < GIFT_PCT;
  const proShown = proGift ? Math.round(PRO_PRICE * (100 - GIFT_PCT)) / 100 : proPrice;
  const lifeShown = giftOn ? GIFT_LIFETIME : FOUNDER_PRICE;

  const startGift = async () => {
    setGiftBusy(true);
    try {
      const r = await axiosInstance.post("/billing/exit-offer");
      setGift(r.data);
      return r.data;
    } catch { return null; } finally { setGiftBusy(false); }
  };
  const onX = () => {
    if (!onClose) return;
    track("paywall_close_clicked", { gift_active: giftOn });
    if (trialMode) onClose();
    else if (giftOn) setGiftStage("leave"); // already has the gift: just confirm
    else if (gift?.offered) onClose(); // gift expired: no second chance
    else setGiftStage("gift");
  };
  const claimGift = async () => {
    track("paywall_gift_claimed");
    if (await startGift()) setGiftStage("reveal");
  };
  const giftToLeave = async () => {
    if (!gift?.offered) await startGift(); // seeing the offer starts the clock
    setGiftStage("leave");
  };
  const stillLeave = () => { track("paywall_gift_left"); setGiftStage(null); onClose?.(); };

  const selected = plan === "founder" && foundersOpen ? "founder" : "pro";

  const [safariHelp, setSafariHelp] = useState(false);
  const openInSafari = () => {
    track("paywall_open_in_safari");
    let token = "";
    try { token = localStorage.getItem("token") || ""; } catch { /* ignore */ }
    const target = token
      ? `${window.location.origin}/auth-success?token=${encodeURIComponent(token)}`
      : `${window.location.origin}/home`;
    // iOS opens x-safari-https:// links in Safari from in-app browsers.
    window.location.href = `x-safari-${target}`;
    // If we're still here a moment later, the app blocked it — show the manual way.
    setTimeout(() => { if (document.visibilityState === "visible") setSafariHelp(true); }, 1200);
  };

  const startCheckout = async () => {
    setError("");
    if (trialMode) {
      track("checkout_started", { plan: "trial", value: 1, ...(discountPct ? { regional_discount_pct: discountPct } : {}) });
      setLoading(true);
      try {
        await openTrialCheckout({ source: "paywall" });
        return; // leaving for Stripe
      } catch (err) {
        if (["trial_used", "trial_unavailable"].includes(err.response?.data?.error)) setTrialOk(false); // show Pro monthly instead
        setError(err.response?.data?.message || t("err_checkout"));
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    try {
      let res;
      if (selected === "founder") {
        track("checkout_started", { plan: "founder_lifetime", value: lifeShown, ...(giftOn ? { gift: true } : {}) });
        res = await axiosInstance.post("/billing/founders/checkout", {
          success_url: RETURN_URL,
          cancel_url: CANCEL_URL,
          ...(giftOn ? { exitOffer: true } : {}),
        });
      } else {
        track("checkout_started", { plan: "pro_monthly", value: proShown, ...(proGift ? { gift: true } : {}), ...(discountPct ? { regional_discount_pct: discountPct } : {}) });
        const referral = getRewardfulReferral();
        res = await axiosInstance.post("/billing/plan/checkout", {
          planName: "Pro",
          interval: "monthly",
          success_url: RETURN_URL,
          cancel_url: CANCEL_URL,
          ...(proGift ? { exitOffer: true } : {}),
          ...(referral ? { referral } : {}),
        });
      }
      if (res.data?.url) { window.location.href = res.data.url; return; }
      setError(t("err_checkout"));
    } catch (err) {
      if (err.response?.data?.error === "sold_out") {
        setFoundersOpen(false);
        setPlan("pro");
        setError(t("err_soldout"));
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || t("err_checkout"));
      }
    }
    setLoading(false);
  };

  // The only way out without paying (wrong account, shared phone…).
  const signOut = () => {
    axiosInstance.post("/auth/logout").catch(() => {});
    resetAnalytics();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });
  };

  if (activating) {
    return (
      <div className="hard-paywall">
        <div className="hard-paywall-card hard-paywall-activating">
          <div className="hard-paywall-spinner" />
          <h1 className="hard-paywall-title">{t("activating_title")}</h1>
          <p className="hard-paywall-fineprint">{t("activating_note")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hard-paywall" role="dialog" aria-modal="true" aria-label={t("aria_choose")}>
      <span className="hpw-blob hpw-blob--a" aria-hidden="true" />
      <span className="hpw-blob hpw-blob--b" aria-hidden="true" />
      <div className="hard-paywall-card">
        <div className="hard-paywall-topbar">
          {onClose ? (
            <button type="button" className="hard-paywall-close" onClick={onX} aria-label={t("close")}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          ) : <span />}
        </div>

        <div className={`hpw-mascot${readyClips.length ? " hpw-mascot--sm" : ""}`}><img src="/mascot.png" alt="Clipbait" /></div>

        {readyClips.length > 0 ? (
          <>
            <h1 className="hard-paywall-title">
              <Trans t={t} i18nKey="ready_title" values={{ count: readyClips.length }} components={{ a: <span className="hard-paywall-accent" /> }}
                defaults="Your <a>{{count}} clips</a> are ready" />
            </h1>
            <div className="hpw-ready">
              {readyClips.slice(0, 3).map((c, i) => (
                <button
                  type="button"
                  key={c._id || i}
                  className={`hpw-ready-clip${zoomedClip === i ? " hpw-ready-clip--zoom" : ""}${zoomedClip !== null && zoomedClip !== i ? " hpw-ready-clip--dim" : ""}`}
                  style={{ "--i": i }}
                  onClick={() => { setZoomedClip(zoomedClip === i ? null : i); setLockShake((p) => ({ i, n: p.n + 1 })); track("paywall_clip_tapped", { index: i }); }}
                  aria-label={`Clip ${i + 1} (locked)`}
                >
                  {c.thumbnailUrl
                    ? <img src={`/api/clips/thumb?u=${encodeURIComponent(c.thumbnailUrl)}`} alt="" />
                    : <video src={c.url} muted playsInline preload="metadata" />}
                  <span key={lockShake.i === i ? lockShake.n : 0} className={`hpw-ready-lock${lockShake.i === i && lockShake.n ? " hpw-ready-lock--shake" : ""}`} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                  </span>
                </button>
              ))}
            </div>
            <p className="hpw-proof">{t("ready_sub", { defaultValue: "Unlock Pro to watch, download and post them." })}</p>
          </>
        ) : showPending ? (
          <>
            <h1 className="hard-paywall-title">
              <Trans t={t} i18nKey="pending_title" components={{ a: <span className="hard-paywall-accent" /> }}
                defaults="Your stream is <a>ready to clip</a>" />
            </h1>
            <div className="hpw-pending">
              <span className="hpw-pending-thumb">
                {pendingRun.thumbnail ? <img src={pendingRun.thumbnail} alt="" />
                  : pendingRun.avatar ? (
                    // No VOD thumbnail (yet): the streamer themself, never our logo.
                    <>
                      <img src={pendingRun.avatar} alt="" className="hpw-pending-avatar-bg" aria-hidden="true" />
                      <img src={pendingRun.avatar} alt="" className="hpw-pending-avatar" />
                    </>
                  ) : <img src="/mascot.png" alt="" className="hpw-pending-mascot" />}
              </span>
              <span className="hpw-pending-text">
                <span className="hpw-pending-kicker">{t("pending_kicker", { defaultValue: "CLIPS START THE MOMENT YOU UNLOCK" })}</span>
                <span className="hpw-pending-name">{pendingRun.title || t("pending_untitled", { defaultValue: "Your stream" })}</span>
              </span>
            </div>
          </>
        ) : (
          <h1 className="hard-paywall-title">
            <Trans t={t} i18nKey="title" components={{ a: <span className="hard-paywall-accent" /> }} />
          </h1>
        )}
        {creators && !readyClips.length && !showPending && (
          <p className="hpw-proof">{t("social_proof", { count: creators.toLocaleString(i18n.language) })}</p>
        )}

        <div className="hpw-platforms" aria-label={t("works_with")}>
          <span className="hpw-platforms-label">{t("works_with")}</span>
          {PLATFORMS.map((pf) => (
            <span key={pf.name} className="hpw-platform">
              <img src={pf.logo} alt="" aria-hidden="true" />{pf.name}
            </span>
          ))}
        </div>

        {/* While the gift banner shows, drop the stats row so it still fits one screen. */}
        {!giftOn && !readyClips.length && <div className="hpw-stats">
          {STATS.map((st) => (
            <div key={st.label} className="hpw-stat">
              <div className="hpw-stat-value">{st.valueKey ? t(st.valueKey) : st.value}</div>
              <div className="hpw-stat-label">{t(st.label)}</div>
            </div>
          ))}
        </div>}

        <div className="hpw-features">
          {FEATURES.map((f) => (
            <div key={f.label} className="hpw-feature">
              <span className="hpw-feature-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{f.icon}</svg>
              </span>
              {t(f.label)}
            </div>
          ))}
        </div>

        {giftOn && (
          <div className="hpw-gift-banner" role="status">
            {t("gift_applied")} · {t("ends_in")} <span className="hpw-countdown">{formatLeft(leftMs)}</span>
          </div>
        )}

        {trialMode ? (
          <div className="hard-paywall-plans">
            <div className="hard-paywall-plan is-selected">
              <span className="hpw-badge">{t("trial_badge", { defaultValue: "⚡ 3-DAY TRIAL" })}</span>
              <span className="hpw-radio" />
              <span className="hpw-plan-text">
                <span className="hpw-plan-name">{t("trial_name", { defaultValue: "Pro trial" })}</span>
                <span className="hpw-plan-note">{t("trial_note", { price: money(proPrice), defaultValue: "then {{price}}/mo · cancel anytime" })}</span>
              </span>
              <span className="hpw-plan-price">
                $1<span>{t("trial_today", { defaultValue: " today" })}</span>
              </span>
            </div>
          </div>
        ) : (
        <div className="hard-paywall-plans" role="radiogroup" aria-label="Plan">
          <button
            type="button"
            role="radio"
            aria-checked={selected === "pro"}
            className={`hard-paywall-plan${selected === "pro" ? " is-selected" : ""}`}
            onClick={() => setPlan("pro")}
          >
            <span className="hpw-badge">{t("recommended")}</span>
            <span className="hpw-radio" />
            <span className="hpw-plan-text">
              <span className="hpw-plan-name">{t("pro_name")}</span>
              <span className="hpw-plan-note">
                {proGift ? t("then_price", { price: money(PRO_PRICE) }) : discountPct ? t("regional", { pct: discountPct }) : t("cancel_anytime")}
              </span>
            </span>
            <span className="hpw-plan-price">
              {(discountPct > 0 || proGift) && <s className="hpw-plan-was">{money(PRO_PRICE)}</s>}
              {money(proShown)}<span>{proGift ? t("first_month") : t("per_month")}</span>
            </span>
          </button>
          {foundersOpen && (
            <button
              type="button"
              role="radio"
              aria-checked={selected === "founder"}
              className={`hard-paywall-plan${selected === "founder" ? " is-selected" : ""}`}
              onClick={() => setPlan("founder")}
            >
              <span className="hard-paywall-limited">
                {spotsLeft != null ? t("limited_left", { count: spotsLeft }) : t("limited")}
              </span>
              <span className="hpw-radio" />
              <span className="hpw-plan-text">
                <span className="hpw-plan-name">{t("lifetime")}</span>
                <span className="hpw-plan-note">{t("lifetime_note")}</span>
              </span>
              <span className="hpw-plan-price">
                {giftOn && <s className="hpw-plan-was">{money(FOUNDER_PRICE)}</s>}
                {money(lifeShown)}<span>{t("per_once")}</span>
              </span>
            </button>
          )}
        </div>
        )}

        {error && <div className="hard-paywall-error">{error}</div>}

        {SHOW_SAFARI_PROMPT && (
          <button type="button" className="hard-paywall-safari" onClick={openInSafari}>
            <svg width="14" height="16" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
            {t("safari")}
          </button>
        )}
        {safariHelp && (
          <p className="hard-paywall-safari-help">
            <Trans t={t} i18nKey="safari_help" components={{ b: <b /> }} />
          </p>
        )}

        <button type="button" className="hard-paywall-cta" onClick={startCheckout} disabled={loading}>
          <span className="hard-paywall-cta-shine" aria-hidden="true" />
          {SPARKLES.map((sp, i) => (
            <span
              key={i}
              className="hard-paywall-sparkle"
              aria-hidden="true"
              style={{ left: sp.left, top: sp.top, animationDelay: sp.delay, fontSize: sp.size }}
            >
              ✦
            </span>
          ))}
          <span className="hard-paywall-cta-label">
            {loading ? t("cta_loading")
              : trialMode ? (showPending ? t("cta_trial_clip", { defaultValue: "Start for $1 & clip it" }) : t("cta_trial", { defaultValue: "Start for $1" }))
              : t("cta")}
          </span>
        </button>

        <p className="hard-paywall-fineprint">
          {trialMode
            ? t("fine_trial", { price: money(proPrice), defaultValue: "120 credits today · {{price}}/mo after 3 days · Cancel anytime" })
            : selected === "founder" ? t("fine_founder") : t("fine_pro")}
        </p>
        <div className="hard-paywall-links" data-paywall-allow="">
          <a href="https://clipbait.ai/terms" target="_blank" rel="noopener noreferrer">{t("terms")}</a>
          <a href="https://clipbait.ai/privacy" target="_blank" rel="noopener noreferrer">{t("privacy")}</a>
          <button type="button" className="hard-paywall-signout" onClick={signOut}>{t("sign_out")}</button>
        </div>
      </div>
      {giftStage && (
        <GiftOffer
          stage={giftStage}
          t={t}
          leftMs={leftMs}
          proPrice={money(Math.round(PRO_PRICE * (100 - GIFT_PCT)) / 100)}
          lifePrice={money(GIFT_LIFETIME)}
          busy={giftBusy}
          onClaim={claimGift}
          onUse={() => setGiftStage(null)}
          onToLeave={giftToLeave}
          onKeep={() => setGiftStage(null)}
          onLeave={stillLeave}
        />
      )}
    </div>
  );
};

export default HardPaywall;
